/**
 * PIMs Scoring Sensitivity Admin Page
 *
 * Admin-only page that simulates how floor / ceiling / pass-threshold
 * changes would affect provider eligibility under the PIMs performance
 * formula. Read-only — values shown are the current live scoring
 * parameters from sp_equip_cache_performance.
 *
 * Reachable only by direct URL (#/admin/pims-sensitivity). Not in any nav.
 */

import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Activity, Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { usePimsDistribution } from '@/hooks/equip/useEquipConfig'
import { calcPerf, calcMedian, calcMean } from '@/lib/pims-scoring-math'

// ---------------------------------------------------------------------------
// Constants — mirror sp_equip_cache_performance CTE "pims_performance"
// ---------------------------------------------------------------------------

const PCP_DEFAULTS = { floor: 2.4, ceiling: 9.0, threshold: 0.89 }
const PSYCH_DEFAULTS = { floor: 0.43, ceiling: 1.9, threshold: 0.89 }

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function fmt(n: number, decimals = 2) {
  return n.toFixed(decimals)
}

// Histogram bar color: red (0%) → green (100%)
function binColor(binMidPerf: number): string {
  const hue = Math.round(binMidPerf * 120)
  return `hsl(${hue}, 75%, 55%)`
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PanelConfig {
  floor: number
  ceiling: number
  threshold: number
}

interface ComputedStats {
  n: number
  passingCount: number
  passRate: number
  medianRaw: number
  meanRaw: number
  medianPerf: number
  avgPerf: number
  bins: number[]
  maxBinCount: number
}

// ---------------------------------------------------------------------------
// useStats — memoised simulation over raw scores
// ---------------------------------------------------------------------------

function useStats(scores: number[], config: PanelConfig): ComputedStats {
  return useMemo(() => {
    const { floor, ceiling, threshold } = config
    if (scores.length === 0) {
      return { n: 0, passingCount: 0, passRate: 0, medianRaw: 0, meanRaw: 0, medianPerf: 0, avgPerf: 0, bins: Array(20).fill(0), maxBinCount: 0 }
    }
    const perfs = scores.map(s => calcPerf(s, floor, ceiling))
    const passingCount = perfs.filter(p => p >= threshold).length
    const passRate = passingCount / perfs.length
    const medianRaw = calcMedian(scores)
    const meanRaw = calcMean(scores)
    const medianPerf = calcPerf(medianRaw, floor, ceiling)
    const avgPerf = perfs.reduce((a, b) => a + b, 0) / perfs.length

    const bins = Array(20).fill(0)
    perfs.forEach(p => {
      const idx = Math.min(Math.floor(p * 20), 19)
      bins[idx]++
    })
    const maxBinCount = Math.max(...bins)

    return { n: scores.length, passingCount, passRate, medianRaw, meanRaw, medianPerf, avgPerf, bins, maxBinCount }
  }, [scores, config.floor, config.ceiling, config.threshold])
}

// ---------------------------------------------------------------------------
// StatChip
// ---------------------------------------------------------------------------

function StatChip({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`flex flex-col items-center justify-center px-2 py-2 rounded-lg border w-full ${accent ? 'bg-primary/5 border-primary/20' : 'bg-muted/40 border-border'}`}>
      <span className="text-xs text-muted-foreground leading-none mb-1">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  )
}

function DualStatChip({ label, primaryLabel, primaryValue, secondaryLabel, secondaryValue }: {
  label: string
  primaryLabel: string
  primaryValue: string
  secondaryLabel: string
  secondaryValue: string
}) {
  return (
    <div className="flex flex-col items-center justify-center px-2 py-2 rounded-lg border w-full bg-muted/40 border-border">
      <span className="text-xs text-muted-foreground leading-none mb-1">{label}</span>
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-xs tabular-nums leading-tight text-foreground">
          <span className="text-muted-foreground">{primaryLabel}</span> <span className="font-semibold">{primaryValue}</span>
        </span>
        <span className="text-xs tabular-nums leading-tight text-foreground">
          <span className="text-muted-foreground">{secondaryLabel}</span> <span className="font-semibold">{secondaryValue}</span>
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AwardEligibilityRow
// ---------------------------------------------------------------------------

function AwardEligibilityRow({ passRate, threshold }: { passRate: number; threshold: number }) {
  const pct = Math.round(passRate * 100)
  const passing = passRate >= threshold
  return (
    <div className="flex items-center justify-between rounded-md px-3 py-2 text-sm border bg-muted/40 border-border">
      <span className="text-foreground">
        {pct}% of providers meet the consistency threshold
      </span>
      <Badge variant="outline" className="border-border text-muted-foreground">
        {passing ? 'PIMs Award Eligible' : 'Below Threshold'}
      </Badge>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Histogram
// ---------------------------------------------------------------------------

function Histogram({ bins, maxBinCount, medianPerf, threshold }: {
  bins: number[]
  maxBinCount: number
  medianPerf: number
  threshold: number
}) {
  const thresholdPct = threshold * 100
  const medianPct = medianPerf * 100
  const flipMedianLabel = medianPerf > 0.8

  return (
    <div className="space-y-1">
      <div className="relative h-28 w-full" style={{ minWidth: 0 }}>
        <div
          className="absolute top-0 bottom-0 w-px z-10"
          style={{
            left: `${thresholdPct}%`,
            borderLeft: '2px dashed',
            borderColor: 'rgb(99,102,241)',
          }}
        />
        <div
          className="absolute top-0 bottom-0 w-px z-10"
          style={{
            left: `${medianPct}%`,
            borderLeft: '2px solid',
            borderColor: 'rgb(245,158,11)',
          }}
        >
          <span
            className="absolute -top-5 text-[10px] font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap"
            style={flipMedianLabel ? { right: '4px' } : { left: '4px' }}
          >
            Median {Math.round(medianPct)}%
          </span>
        </div>

        <div className="absolute inset-0 flex items-end gap-px px-px">
          {bins.map((count, i) => {
            const binMidPerf = (i + 0.5) / 20
            const heightPct = maxBinCount > 0 ? (count / maxBinCount) * 100 : 0
            return (
              <div
                key={i}
                title={`${i * 5}–${(i + 1) * 5}%: ${count} providers`}
                className="flex-1 rounded-t-sm transition-all duration-300 cursor-default"
                style={{
                  height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%`,
                  backgroundColor: binColor(binMidPerf),
                  opacity: count === 0 ? 0.15 : 1,
                }}
              />
            )
          })}
        </div>
      </div>

      <div className="flex justify-between text-[10px] text-muted-foreground px-px">
        <span>0%</span>
        <span>25%</span>
        <span>50%</span>
        <span>75%</span>
        <span>100%</span>
      </div>

      <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground pt-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-0 border-t-2 border-dashed border-indigo-500" />
          Pass threshold ({Math.round(threshold * 100)}%)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-0 border-t-2 border-amber-500" />
          Median score
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SliderField
// ---------------------------------------------------------------------------

interface SliderFieldProps {
  label: string
  hint: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  displayValue?: string
  changed: boolean
  onChange: (v: number) => void
}

function SliderField({ label, hint, value, min, max, step, unit, displayValue, changed, onChange }: SliderFieldProps) {
  const shown = displayValue ?? `${fmt(value)} ${unit}`
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium text-foreground">{label}</label>
        <span className={`text-xs tabular-nums ${changed ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
          {shown}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
        className="w-full"
      />
      <p className="text-[10px] text-muted-foreground leading-tight">{hint}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PanelSimulator
// ---------------------------------------------------------------------------

interface PanelSimulatorProps {
  label: string
  scores: number[]
  defaults: { floor: number; ceiling: number; threshold: number }
  floorRange: [number, number]
  ceilingRange: [number, number]
  unit: string
}

function PanelSimulator({ label, scores, defaults, floorRange, ceilingRange, unit }: PanelSimulatorProps) {
  const [floor, setFloor] = useState(defaults.floor)
  const [ceiling, setCeiling] = useState(defaults.ceiling)

  const config: PanelConfig = { floor, ceiling, threshold: defaults.threshold }
  const stats = useStats(scores, config)

  const isDefaultFloor = floor === defaults.floor
  const isDefaultCeiling = ceiling === defaults.ceiling

  function reset() {
    setFloor(defaults.floor)
    setCeiling(defaults.ceiling)
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{label}</h4>
        <button
          onClick={reset}
          disabled={isDefaultFloor && isDefaultCeiling}
          className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Reset to defaults
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SliderField
          label="Floor"
          hint={`Score ≤ floor = 100% (best). Default: ${defaults.floor}`}
          value={floor}
          min={floorRange[0]}
          max={floorRange[1]}
          step={0.01}
          unit={unit}
          changed={!isDefaultFloor}
          onChange={v => { if (v < ceiling) setFloor(v) }}
        />
        <SliderField
          label="Ceiling"
          hint={`Score ≥ ceiling = 0% (worst). Default: ${defaults.ceiling}`}
          value={ceiling}
          min={floorRange[0]}
          max={ceilingRange[1]}
          step={0.01}
          unit={unit}
          changed={!isDefaultCeiling}
          onChange={v => { if (v > floor) setCeiling(v) }}
        />
      </div>

      <div className="grid grid-cols-4 gap-2 items-stretch">
        <StatChip label="Providers" value={String(stats.n)} />
        <DualStatChip
          label="Raw Score"
          primaryLabel="Med"
          primaryValue={fmt(stats.medianRaw)}
          secondaryLabel="Mean"
          secondaryValue={fmt(stats.meanRaw)}
        />
        <DualStatChip
          label="Perf"
          primaryLabel="Med"
          primaryValue={`${Math.round(stats.medianPerf * 100)}%`}
          secondaryLabel="Avg"
          secondaryValue={`${Math.round(stats.avgPerf * 100)}%`}
        />
        <StatChip label="Passing" value={`${stats.passingCount} / ${stats.n}`} accent />
      </div>

      <AwardEligibilityRow passRate={stats.passRate} threshold={defaults.threshold} />

      <Histogram
        bins={stats.bins}
        maxBinCount={stats.maxBinCount}
        medianPerf={stats.medianPerf}
        threshold={defaults.threshold}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PimsScoringSensitivity() {
  const { data, isLoading, isError } = usePimsDistribution()

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-5 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">PIMs Scoring Sensitivity</h1>
            <p className="text-xs text-muted-foreground">
              Admin tool — preview how floor / ceiling / pass-threshold changes affect provider eligibility.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Scoring Simulator</CardTitle>
                  <CardDescription>
                    Read-only preview — values shown are the current live scoring parameters.
                    Sliders are session-scoped; changes do not persist.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {isLoading && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Skeleton className="h-72 w-full rounded-lg" />
                  <Skeleton className="h-72 w-full rounded-lg" />
                </div>
              )}

              {isError && (
                <p className="text-sm text-destructive">Failed to load PIMs score distribution.</p>
              )}

              {data && (
                <>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <PanelSimulator
                      label="PCP Panel"
                      scores={data.pcp_scores}
                      defaults={PCP_DEFAULTS}
                      floorRange={[0, 5]}
                      ceilingRange={[0, 15]}
                      unit="pts"
                    />
                    <PanelSimulator
                      label="Psych Panel"
                      scores={data.psych_scores}
                      defaults={PSYCH_DEFAULTS}
                      floorRange={[0, 1]}
                      ceilingRange={[0, 3]}
                      unit="ratio"
                    />
                  </div>

                  <p className="text-xs text-muted-foreground border-l-2 border-border pl-3 py-2 leading-relaxed">
                    <span className="font-semibold text-foreground">A note on further movement.</span>{' '}
                    A mean below ~2 likely reflects real progress, but we don't know how close we are to
                    the clinical floor. Tightening these sliders risks pushing providers toward gains
                    that aren't actually available. The higher-leverage move could be to re-weight the
                    underlying audit toward antipsychotics and anxiolytics — where we are still eager to
                    see improvement — rather than compressing the band further.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
